import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import {
    ReactFlow,
    ReactFlowProvider,
    useNodesState,
    useEdgesState,
    addEdge,
    MiniMap,
    Controls,
    Background,
    Panel,
    useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { Box, Paper, Typography, Button, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, useTheme, alpha } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Loading from '../Loading';

// React Flow nodes are built from inline style objects, so the palette has to
// be resolved up front and threaded through the node factory.
const flowPalette = (theme) => {
    const { palette } = theme;
    const isDark = palette.mode === 'dark';
    return {
        nodeBg: palette.surface.raised,
        headerGradient: isDark
            ? `linear-gradient(135deg, ${palette.surface.subtle}, ${palette.surface.raised})`
            : 'linear-gradient(135deg, #f5f9ff, #e1ecff)',
        accent: isDark ? palette.primary.light : '#4A90E2',
        text: palette.text.primary,
        textSecondary: palette.text.secondary,
        textMuted: palette.text.disabled,
        border: palette.surface.border,
        wellBg: palette.surface.sunken,
        footerBg: palette.surface.subtle,
        onAccent: '#ffffff',
        shadow: isDark ? '0 6px 14px rgba(0, 0, 0, 0.5)' : '0 6px 8px rgba(0, 0, 0, 0.1)',
        shadowLg: isDark ? '0 8px 18px rgba(0, 0, 0, 0.55)' : '0 8px 10px rgba(0, 0, 0, 0.1)',
    };
};

const FlowLogicInner = ({ nodes, edges, onInitHandler }) => {
    const { getNodes } = useReactFlow();
    const [isExporting, setIsExporting] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const captureImage = async () => {
        const viewportElement = document.querySelector('.react-flow__viewport');
        if (!viewportElement) {
            throw new Error('Could not find React Flow viewport element');
        }

        const currentNodes = getNodes();
        if (!currentNodes || currentNodes.length === 0) {
            throw new Error('No nodes to export');
        }

        const filterContainers = viewportElement.querySelectorAll('.flow-node-filters');
        const savedFilterStyles = [];
        filterContainers.forEach((container) => {
            savedFilterStyles.push({
                element: container,
                maxHeight: container.style.maxHeight,
                overflowY: container.style.overflowY,
            });
            container.style.maxHeight = 'none';
            container.style.overflowY = 'visible';
        });

        const nodeWrappers = viewportElement.querySelectorAll('.react-flow__node');
        const savedNodeStyles = [];
        nodeWrappers.forEach((nodeEl) => {
            savedNodeStyles.push({
                element: nodeEl,
                height: nodeEl.style.height,
                overflow: nodeEl.style.overflow,
            });
            nodeEl.style.height = 'auto';
            nodeEl.style.overflow = 'visible';
        });

        // Wait for browser reflow after style changes
        await new Promise(resolve => setTimeout(resolve, 50));

        // Recalculate bounds using actual DOM dimensions after expansion
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        currentNodes.forEach((node, idx) => {
            const nodeEl = nodeWrappers[idx];
            const w = nodeEl ? nodeEl.offsetWidth : (node.measured?.width || node.style?.width || 0);
            const h = nodeEl ? nodeEl.offsetHeight : (node.measured?.height || node.style?.height || 0);
            minX = Math.min(minX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxX = Math.max(maxX, node.position.x + w);
            maxY = Math.max(maxY, node.position.y + h);
        });

        const padding = 100;
        const imageWidth = (maxX - minX) + padding * 2;
        const imageHeight = (maxY - minY) + padding * 2;

        try {
            const dataUrl = await toPng(viewportElement, {
                // Exported images stay light regardless of the UI mode.
                backgroundColor: '#ffffff',
                width: imageWidth,
                height: imageHeight,
                style: {
                    width: `${imageWidth}px`,
                    height: `${imageHeight}px`,
                    transform: `translate(${padding - minX}px, ${padding - minY}px) scale(1)`,
                },
            });

            return { dataUrl, imageWidth, imageHeight };
        } finally {
            // Restore original styles
            savedFilterStyles.forEach(({ element, maxHeight, overflowY }) => {
                element.style.maxHeight = maxHeight;
                element.style.overflowY = overflowY;
            });
            savedNodeStyles.forEach(({ element, height, overflow }) => {
                element.style.height = height;
                element.style.overflow = overflow;
            });
        }
    };

    const handleDownloadPNG = async () => {
        setIsExporting(true);
        try {
            const { dataUrl } = await captureImage();
            const link = document.createElement('a');
            link.setAttribute('download', 'query-flow-logic.png');
            link.setAttribute('href', dataUrl);
            link.click();
        } catch (error) {
            console.error('Error exporting PNG:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleDownloadPDF = async () => {
        setIsExporting(true);
        try {
            const { dataUrl, imageWidth, imageHeight } = await captureImage();
            const orientation = imageWidth > imageHeight ? 'landscape' : 'portrait';
            const pdf = new jsPDF(orientation, 'px', [imageWidth, imageHeight]);
            pdf.addImage(dataUrl, 'PNG', 0, 0, imageWidth, imageHeight);
            pdf.save('query-flow-logic.pdf');
        } catch (error) {
            console.error('Error exporting PDF:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onInit={onInitHandler}
            fitView
            fitViewOptions={{
                padding: 0.2,
                includeHiddenNodes: true
            }}
        >
            <Controls />
            <Panel position="top-right">
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={isExporting ? <CircularProgress size={16} color="primary" /> : <DownloadIcon />}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    disabled={isExporting || !nodes || nodes.length === 0}
                    sx={{
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        backgroundColor: 'background.paper',
                        textTransform: 'none',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: alpha('#04aeea', 0.12),
                        },
                    }}
                >
                    Download
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                    <MenuItem onClick={() => { setAnchorEl(null); handleDownloadPNG(); }}>
                        <ListItemIcon><ImageIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Download as PNG</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => { setAnchorEl(null); handleDownloadPDF(); }}>
                        <ListItemIcon><PictureAsPdfIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Download as PDF</ListItemText>
                    </MenuItem>
                </Menu>
            </Panel>
        </ReactFlow>
    );
};

// ---------------------------------------------------------------------------

const FlowLogic = ({ filterSteps, metadata, initialPatientCount, isPatientCountLoading, filterVersion }) => {
    const reactFlowInstanceRef = useRef(null);
    const theme = useTheme();
    const c = flowPalette(theme);

    const transformFiltersToFlow = (filterSteps, metadata, initialPatientCount) => {
        if (!filterSteps || filterSteps.length === 0) {
            return { nodes: [], edges: [] };
        }

        const nodes = [];
        const edges = [];

        const containerWidth = 900;
        const nodeWidth = 500;
        const xPosition = (containerWidth - nodeWidth) / 2;

        const baseVerticalSpacing = 150;

        let yPosition = 100;

        // Database node
        const dbNodeId = 'db-0';
        const dbNodeHeight = 150;
        nodes.push({
            id: dbNodeId,
            type: 'default',
            data: {
                label: (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        background: c.headerGradient,
                        padding: '15px',
                        boxSizing: 'border-box',
                        borderRadius: '14px 14px 0 0',
                    }}>
                        <div style={{
                            fontWeight: 'bold',
                            fontSize: '20px',
                            color: c.accent,
                            textAlign: 'center',
                            paddingBottom: '10px',
                            borderBottom: `1px solid ${c.border}`,
                            width: '100%',
                        }}>
                            {metadata?.db || 'Database'}
                        </div>

                        <div style={{
                            background: c.nodeBg,
                            padding: '10px',
                            borderRadius: '8px',
                            textAlign: 'center',
                            fontSize: '14px',
                            marginTop: 'auto',
                            color: c.accent,
                        }}>
                            <span style={{ fontWeight: 'bold' }}>Patient Count: </span>
                            {initialPatientCount && initialPatientCount !== "0" && initialPatientCount !== 0 ? (
                                <span style={{ fontWeight: 'bold' }}>{initialPatientCount}</span>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <span style={{
                                        fontWeight: 'bold',
                                        color: c.textSecondary,
                                        fontSize: '12px'
                                    }}>
                                        Not Available
                                    </span>
                                    <span style={{
                                        fontSize: '11px',
                                        color: c.textMuted,
                                        fontStyle: 'italic'
                                    }}>
                                        Run query to get current count
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )
            },
            position: { x: xPosition, y: yPosition },
            style: {
                background: c.headerGradient,
                border: `2px solid ${c.accent}`,
                borderRadius: '12px',
                padding: '0px',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'center',
                boxShadow: c.shadowLg,
                width: nodeWidth,
                height: dbNodeHeight,
                boxSizing: 'border-box',
            },
        });

        // Track cumulative y position
        yPosition += dbNodeHeight + baseVerticalSpacing;

        // Process each filter step
        filterSteps.forEach((step, index) => {
            const categoryId = `category-${index}`;
            const categoryColor = step.color || '#04aeea';

            // Calculate how many rows of filters we need based on content
            const filtersCount = step.filters?.length || 0;

            // Calculate total subfilters to add to height
            let totalSubfilters = 0;
            if (step.filters) {
                step.filters.forEach(filter => {
                    if (filter.rows && filter.rows.length > 0) {
                        totalSubfilters += filter.rows.length;
                    }
                });
            }

            const headerHeight = 45;
            const subheaderHeight = 45;
            const footerHeight = 45;
            const baseFilterHeight = 80;
            const subfilterHeight = 30;
            const maxFilterContainerHeight = 350;
            const minNodeHeight = 250;
            const scrollIndicatorHeight = 28;

            // Calculate rows needed - 1 filter per row (vertical stack)
            const rowsNeeded = filtersCount;

            // Calculate content height with padding
            const contentHeight = (rowsNeeded * baseFilterHeight) + (totalSubfilters * subfilterHeight) + 40;

            // Cap content height and enable scrolling when filters exceed visible area
            const needsScroll = contentHeight > maxFilterContainerHeight;
            const effectiveContentHeight = needsScroll ? maxFilterContainerHeight : contentHeight;
            const scrollSpace = needsScroll ? scrollIndicatorHeight : 0;

            // Total node height (buffer only needed when height is estimated, not when capped)
            const heightBuffer = needsScroll ? 0 : 50;
            const nodeHeight = Math.max(minNodeHeight,
                headerHeight + subheaderHeight + effectiveContentHeight + scrollSpace + footerHeight + heightBuffer);

            // Create the node
            nodes.push({
                id: categoryId,
                type: 'default',
                data: {
                    label: (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: '100%',
                        }}>
                            {/* Header with combinator context */}
                            <div style={{
                                fontWeight: 'bold',
                                fontSize: '16px',
                                color: c.onAccent,
                                textAlign: 'center',
                                padding: '8px',
                                backgroundColor: categoryColor,
                                borderRadius: '8px 8px 0 0',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                position: 'relative',
                            }}>
                                {step.combinator === 'or'
                                    ? 'MUST MATCH AT LEAST ONE CONDITION'
                                    : 'MUST MATCH ALL CONDITIONS'}
                                <span style={{
                                    position: 'absolute',
                                    right: '10px',
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                    borderRadius: '10px',
                                    padding: '1px 8px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                }}>
                                    {filtersCount}
                                </span>
                            </div>

                            {/* Category name as a subheader */}
                            <div style={{
                                fontWeight: 'bold',
                                fontSize: '18px',
                                color: c.accent,
                                textAlign: 'center',
                                padding: '10px 5px',
                                borderBottom: `1px solid ${c.border}`,
                            }}>
                                {step.category}
                            </div>

                            {/* Filters Section - Scrollable when exceeding max height */}
                            <div className="flow-node-filters nodrag nowheel" style={{
                                display: 'flex',
                                flexGrow: needsScroll ? 0 : 1,
                                flexShrink: 1,
                                flexDirection: 'column',
                                padding: '15px',
                                width: '100%',
                                alignItems: 'center',
                                position: 'relative',
                                maxHeight: needsScroll ? `${maxFilterContainerHeight}px` : 'none',
                                overflowY: needsScroll ? 'auto' : 'visible',
                                boxSizing: 'border-box',
                                cursor: 'default',
                            }}>
                                {step.filters && step.filters.map((filter, idx) => (
                                    <div key={filter.id || idx} style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        margin: '5px',
                                        padding: '15px',
                                        border: `1px solid ${c.border}`,
                                        borderRadius: '8px',
                                        width: '90%',
                                        position: 'relative',
                                        backgroundColor: c.wellBg,
                                        boxShadow: c.shadow,
                                        alignSelf: 'center',
                                    }}>
                                        {/* Main Filter */}
                                        <div style={{
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            textAlign: 'center',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word'
                                        }}>
                                            {filter.friendlyName} {filter.operator} {filter.value}
                                        </div>

                                        {/* Subfilters */}
                                        {filter.rows && filter.rows.length > 0 && (
                                            <div style={{
                                                marginTop: '10px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderLeft: `2px solid ${c.accent}`,
                                                paddingLeft: '10px',
                                                position: 'relative',
                                            }}>
                                                {filter.rows.map((subFilter, subIndex) => (
                                                    <div key={subFilter.id || `sub-${subIndex}`} style={{
                                                        fontSize: '12px',
                                                        color: c.textSecondary,
                                                        marginBottom: subIndex < filter.rows.length - 1 ? '5px' : '0',
                                                        wordBreak: 'break-word',
                                                        overflowWrap: 'break-word'
                                                    }}>
                                                        {subFilter.friendlyName} {subFilter.operator} {subFilter.value === true ? "true" : subFilter.value === false ? "false" : subFilter.value}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Scroll indicator - shown when filters exceed visible area */}
                            {needsScroll && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '4px 0',
                                    fontSize: '11px',
                                    color: c.textMuted,
                                    borderTop: `1px solid ${c.border}`,
                                    backgroundColor: c.wellBg,
                                    width: '100%',
                                }}>
                                    <span style={{
                                        background: categoryColor,
                                        color: c.onAccent,
                                        borderRadius: '10px',
                                        padding: '2px 8px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        marginRight: '6px',
                                    }}>
                                        {filtersCount}
                                    </span>
                                    filters total — scroll to see all
                                </div>
                            )}

                            {/* Footer: Patient Count - Positioned at the bottom */}
                            <div style={{
                                background: c.footerBg,
                                padding: '10px',
                                borderRadius: '0 0 8px 8px',
                                textAlign: 'center',
                                fontSize: '14px',
                                marginTop: needsScroll ? '0' : 'auto',
                                borderTop: `1px solid ${c.border}`,
                                width: '100%',
                                color: c.accent,
                            }}>
                                <span style={{ fontWeight: 'bold' }}>Patient Count: </span>
                                <span style={{ fontWeight: 'bold' }}>{step.patientCount}</span>
                            </div>
                        </div>
                    ),
                },
                position: { x: xPosition, y: yPosition },
                style: {
                    background: c.nodeBg,
                    border: `2px solid ${categoryColor}`,
                    borderRadius: '12px',
                    padding: '0',
                    fontSize: '14px',
                    width: nodeWidth,
                    height: nodeHeight,
                    boxShadow: c.shadow,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            });

            // Add edge connections
            edges.push({
                id: `e${index === 0 ? dbNodeId : `category-${index - 1}`}-${categoryId}`,
                source: index === 0 ? dbNodeId : `category-${index - 1}`,
                target: categoryId,
                type: 'smoothstep',
                animated: true,
                label: index > 0 ? 'AND' : '',
                labelBgPadding: [8, 4],
                labelBgBorderRadius: 6,
                // labelBgStyle: {
                //     fill: '#4A90E2',
                //     fillOpacity: 1
                // },
                labelStyle: {
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: c.text,
                    padding: '4px 8px',
                    textTransform: 'uppercase',
                },
                style: {
                    stroke: c.accent,
                    strokeWidth: 2,
                    strokeDasharray: '5,5'
                },
            });

            // Update y position for next node
            yPosition += nodeHeight + baseVerticalSpacing;
        });

        return { nodes, edges };
    };



    const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
        return transformFiltersToFlow(filterSteps, metadata, initialPatientCount);
    }, [filterSteps, metadata, initialPatientCount, theme.palette.mode]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge(params, eds)),
        [setEdges]
    );

    useEffect(() => {
        if (filterSteps.length === 0) {
            setNodes([]);
            setEdges([]);
        } else {
            const { nodes: newNodes, edges: newEdges } = transformFiltersToFlow(
                filterSteps,
                metadata,
                initialPatientCount
            );
            setNodes(newNodes);
            setEdges(newEdges);
        }
    }, [filterSteps, metadata, initialPatientCount, setNodes, setEdges, filterVersion, theme.palette.mode]);

    useEffect(() => {
        if (nodes.length > 0 && reactFlowInstanceRef.current) {
            // timeout to ensure all rendering is complete before firing fitView
            setTimeout(() => {
                reactFlowInstanceRef.current.fitView({
                    padding: 0.2,
                    includeHiddenNodes: true,
                    minZoom: 0.5,
                    maxZoom: 1.5
                });
            }, 300);
        }
    }, [nodes.length]);

    return (
        <Box sx={{
            width: "100%",
            height: "80vh",
            position: "relative"
        }}>
            {isPatientCountLoading && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(theme.palette.background.default, 0.7),
                        zIndex: 10,
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            padding: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                            borderRadius: 2
                        }}
                    >
                        <Loading />
                        <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
                            Analyzing patient data
                        </Typography>
                    </Paper>
                </Box>
            )}
            <ReactFlowProvider>
                <FlowLogicInner
                    nodes={nodes}
                    edges={edges}
                    onInitHandler={(instance) => {
                        reactFlowInstanceRef.current = instance;
                    }}
                />
            </ReactFlowProvider>
        </Box>
    );
};
export default FlowLogic;
